import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// ─── Pricing (USD) ────────────────────────────────────────────────────────────

const PRICE = {
  length:       { Midi: 3200, "Floor-length": 5500, Cathedral: 7500 } as const,
  sections:     { Sleeves: 700, Train: 900, Veil: 500, Feet: 350 } as const,
  material:     { "Heritage Linen": 0, "Pima Cotton": 1000, "Lotus Silk": 8800 } as const,
  construction: { "Fabric-lined": 0, "Full crochet": 4500 } as const,
};

// ─── Time (weeks) ─────────────────────────────────────────────────────────────

const WEEKS = {
  length:       { Midi: 16, "Floor-length": 22, Cathedral: 30 } as const,
  sections:     { Sleeves: 3, Train: 2, Veil: 2, Feet: 1 } as const,
  materialMult: { "Heritage Linen": 1, "Pima Cotton": 1, "Lotus Silk": 1.3 } as const,
  constructMult:{ "Full crochet": 1, "Fabric-lined": 0.78 } as const,
};

const MIN_PRICE = 2300;
const MAX_PRICE = 15400;

// ─── Types ────────────────────────────────────────────────────────────────────

type Sel = {
  length:       keyof typeof PRICE.length;
  sections:     Array<keyof typeof PRICE.sections>;
  material:     keyof typeof PRICE.material;
  construction: keyof typeof PRICE.construction;
  afterWedding: "Yours forever" | "Just for the day";
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const stepNum = (n: string) => (
  <span
    className="font-display leading-none select-none shrink-0 text-foreground/[0.07]"
    style={{ fontSize: "clamp(4rem, 10vw, 6.5rem)" }}
  >
    {n}
  </span>
);

const fmt = (n: number) =>
  n >= 10000
    ? `$${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`
    : `$${n.toLocaleString()}`;

// Small animated description swapped per selection
const Desc = ({ text, id }: { text: string; id: string }) => (
  <AnimatePresence mode="wait">
    <motion.p
      key={id}
      initial={{ opacity: 0, y: 3 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.22, ease: "easeOut" }}
      className="font-body text-[12px] text-muted-foreground/80 leading-relaxed italic"
>
  {text}
    </motion.p>
  </AnimatePresence>
);

// ─── Section ─────────────────────────────────────────────────────────────────

const SustainabilitySection = () => {
  const { t } = useTranslation();

  const [sel, setSel] = useState<Sel>({
    length:       "Floor-length",
    sections:     [],
    material:     "Pima Cotton",
    construction: "Full crochet",
    afterWedding: "Yours forever",
  });

  // Track the last saved snapshot to show save state
  const [savedSel, setSavedSel] = useState<Sel | null>(null);

  const selMatch = (a: Sel, b: Sel) =>
    a.length === b.length &&
    a.construction === b.construction &&
    a.material === b.material &&
    a.afterWedding === b.afterWedding &&
    a.sections.length === b.sections.length &&
    a.sections.every((s) => b.sections.includes(s));

  const isSaved = savedSel !== null && selMatch(savedSel, sel);

  // ── Compute price ──────────────────────────────────────────────────────────
  const totalPrice =
    PRICE.length[sel.length] +
    sel.sections.reduce((s, v) => s + PRICE.sections[v], 0) +
    PRICE.material[sel.material] +
    PRICE.construction[sel.construction];

  // ── Compute time ───────────────────────────────────────────────────────────
  const baseWeeks =
    WEEKS.length[sel.length] +
    sel.sections.reduce((s, v) => s + WEEKS.sections[v], 0);
  const totalWeeks = Math.round(
    baseWeeks * WEEKS.materialMult[sel.material] * WEEKS.constructMult[sel.construction]
  );
  const rawMonths = totalWeeks / 4.33;
  const monthsDisplay = `~${Math.max(1, Math.round(rawMonths))}`;

  // ── Bar & rebate ───────────────────────────────────────────────────────────
  const barHeight = Math.max(4, Math.round(((totalPrice - MIN_PRICE) / (MAX_PRICE - MIN_PRICE)) * 100));
  const netPrice  = sel.afterWedding === "Just for the day"
    ? Math.round((totalPrice * 0.6) / 50) * 50
    : null;

  // ── Description lookups (translated) ──────────────────────────────────────
  const lengthDescs: Record<Sel["length"], string> = {
    "Midi":         t("sustainability.midiDesc"),
    "Floor-length": t("sustainability.floorDesc"),
    "Cathedral":    t("sustainability.cathedralDesc"),
  };

  const constructionDescs: Record<Sel["construction"], string> = {
    "Fabric-lined": t("sustainability.linedDesc"),
    "Full crochet": t("sustainability.fullCrochetDesc"),
  };

  const materialDescs: Record<Sel["material"], string> = {
    "Heritage Linen": t("sustainability.linenDesc"),
    "Pima Cotton":    t("sustainability.cottonDesc"),
    "Lotus Silk":     t("sustainability.silkDesc"),
  };

  const afterWeddingDescs: Record<Sel["afterWedding"], string> = {
    "Yours forever":    t("sustainability.yoursForeverDesc"),
    "Just for the day": t("sustainability.justForDayDesc"),
  };

  const sectionDesc = (sections: Array<keyof typeof PRICE.sections>): string => {
    if (sections.length === 0) return t("sustainability.extensionsNote");
    const has = (s: keyof typeof PRICE.sections) => sections.includes(s);
    if (sections.length === 1) {
      if (has("Sleeves")) return t("sustainability.sleevesDesc");
      if (has("Train"))   return t("sustainability.trainDesc");
      if (has("Veil"))    return t("sustainability.veilDesc");
      if (has("Feet"))    return t("sustainability.feetDesc");
    }
    // Multi-combination descriptions (kept concise)
    if (sections.length === 2) {
      if (!has("Veil") && !has("Feet"))    return "Sleeves and train — presence from shoulder to floor, with a dramatic trailing finish.";
      if (!has("Train") && !has("Feet"))   return "Sleeves and veil — hand-worked from shoulder to hair. A layered devotion.";
      if (!has("Sleeves") && !has("Feet")) return "Train and veil — trailing beauty from hem to crown.";
      if (has("Sleeves") && has("Feet"))   return "From hands to feet — a fully hand-worked presence on every visible surface.";
      if (has("Train") && has("Feet"))     return "Train and foot adornments — trailing beauty from hem to the ground beneath you.";
      return "Veil and foot lace — from crown to toe, every detail considered.";
    }
    if (sections.length === 3) {
      if (!has("Feet"))  return "Sleeves, train, and veil — a complete commission. Every element hand-worked, nothing left out.";
      if (!has("Veil"))  return "Sleeves, train, and foot adornments — presence from shoulder to ground, with a trailing finish.";
      if (!has("Train")) return "Sleeves, veil, and foot lace — hand-worked from shoulder to hair to toe.";
      return "Train, veil, and foot lace — trailing beauty from hem to crown, grounded to the last stitch.";
    }
    return "The full devotion — sleeves, train, veil, and foot adornments. Every surface hand-worked, nothing unconsidered.";
  };

  // ── Save to sessionStorage ─────────────────────────────────────────────────
  const saveQuotation = () => {
    if (isSaved) return;
    try {
      sessionStorage.setItem("ritual_quotation", JSON.stringify({
        sel,
        totalPrice,
        netPrice,
        formattedPrice: fmt(totalPrice),
        monthsDisplay,
      }));
    } catch { /* noop */ }
    setSavedSel({ ...sel, sections: [...sel.sections] });
  };

  const toggleSection = (v: keyof typeof PRICE.sections) =>
    setSel((p) => ({
      ...p,
      sections: p.sections.includes(v)
        ? p.sections.filter((s) => s !== v)
        : [...p.sections, v],
    }));

  const chip = (active: boolean) =>
    `font-body text-xs px-3 py-1.5 border transition-all duration-200 cursor-pointer select-none ${
      active
        ? "border-gold text-gold bg-gold/5"
        : "border-border text-foreground/60 bg-background hover:border-foreground/30 hover:text-foreground/80"
    }`;

  // ── Thermometer label (shared) ─────────────────────────────────────────────
  const thermoLabel = (
    <AnimatePresence mode="wait">
      <motion.div
        key={`${totalPrice}-${totalWeeks}`}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col items-center gap-0.5"
      >
        <p className="font-display text-sm text-gold leading-tight">{fmt(totalPrice)}</p>
        <p className="font-body text-[8px] text-muted-foreground/45 text-center">
          {monthsDisplay} months
        </p>
        {netPrice !== null && (
          <p className="font-body text-[8px] text-gold/55 text-center leading-tight mt-1">
            ~{fmt(netPrice)} net<br />if returned
          </p>
        )}
      </motion.div>
    </AnimatePresence>
  );

  return (
    <section className="w-full py-24 md:py-40 px-6 bg-secondary">
      <div className="max-w-3xl mx-auto">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="mb-24"
        >
          <div className="w-12 h-px bg-gold mb-8" />
          <p className="font-body text-xs tracking-[0.3em] uppercase text-gold mb-4">
            {t("sustainability.overline")}
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-foreground">
            {t("sustainability.title")}
          </h2>
        </motion.div>

        {/* ══════════════════════════════════════════════════════════════════════
            01 — The Conversation
        ══════════════════════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-20"
        >
          <div className="flex gap-6 md:gap-10 items-start">
            {stepNum(t("sustainability.step1Number"))}
            <div className="pt-2 md:pt-4">
              <h3 className="font-display text-2xl md:text-3xl text-foreground mb-4">
                {t("sustainability.step1Title")}
              </h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-md">
                {t("sustainability.step1Text")}
              </p>
            </div>
          </div>
          <div className="mt-12 h-px bg-border" />
        </motion.div>

        {/* ══════════════════════════════════════════════════════════════════════
            02 — The Quotation (interactive)
        ══════════════════════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-20"
        >
          <div className="flex gap-6 md:gap-10 items-start mb-10">
            {stepNum(t("sustainability.step2Number"))}
            <div className="pt-2 md:pt-4">
              <h3 className="font-display text-2xl md:text-3xl text-foreground mb-4">
                {t("sustainability.step2Title")}
              </h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-md">
                {t("sustainability.step2Subtitle")}
              </p>
            </div>
          </div>

          {/* ── Horizontal thermometer (mobile) — shown ABOVE factor rows ── */}
          <div className="md:hidden mb-5 border border-border px-5 py-5">
            <div className="flex items-center justify-between mb-4">
              <p className="font-body text-[10px] tracking-[0.12em] uppercase text-muted-foreground/50">
                {t("sustainability.accessible")}
              </p>
              <AnimatePresence mode="wait">
                <motion.div
                  key={`mobile-${totalPrice}-${totalWeeks}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col items-center gap-1"
                >
                  <p className="font-display text-xl text-gold leading-tight">{fmt(totalPrice)}</p>
                  <p className="font-body text-[11px] text-muted-foreground/55 text-center">{monthsDisplay} months</p>
                  {netPrice !== null && (
                    <p className="font-body text-[11px] text-gold/60 text-center">~{fmt(netPrice)} net if returned</p>
                  )}
                </motion.div>
              </AnimatePresence>
              <p className="font-body text-[10px] tracking-[0.12em] uppercase text-muted-foreground/50">
                {t("sustainability.precious")}
              </p>
            </div>
            <div className="relative h-0.5 w-full bg-border">
              <motion.div
                className="absolute left-0 top-0 h-full bg-gold"
                animate={{ width: `${barHeight}%` }}
                transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
              />
              <motion.div
                className="absolute top-1/2 -translate-y-1/2 w-[9px] h-[9px] rounded-full bg-gold"
                animate={{ left: `calc(${barHeight}% - 4.5px)` }}
                transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
              />
            </div>
          </div>

          {/* Panel: factors + thermometer */}
          <div className="flex gap-5 items-start">

            {/* Factor rows */}
            <div className="flex-1 border border-border divide-y divide-border">

              {/* Length */}
              <div className="grid grid-cols-1 md:grid-cols-[130px_1fr] gap-3 md:gap-5 px-5 py-5">
                <p className="font-body text-xs tracking-[0.22em] uppercase text-gold pt-0.5">
                  {t("sustainability.lengthLabel")}
                </p>
                <div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(["Midi", "Floor-length", "Cathedral"] as const).map((opt) => (
                      <button key={opt} onClick={() => setSel((p) => ({ ...p, length: opt }))} className={chip(sel.length === opt)}>
                        {t(`sustainability.${opt === "Floor-length" ? "floorLabel" : opt === "Midi" ? "midiLabel" : "cathedralLabel"}`)}
                      </button>
                    ))}
                  </div>
                  <Desc text={lengthDescs[sel.length]} id={sel.length} />
                </div>
              </div>

              {/* Construction */}
              <div className="grid grid-cols-1 md:grid-cols-[130px_1fr] gap-3 md:gap-5 px-5 py-5">
                <p className="font-body text-xs tracking-[0.22em] uppercase text-gold pt-0.5">
                  {t("sustainability.constructionLabel")}
                </p>
                <div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(["Fabric-lined", "Full crochet"] as const).map((opt) => (
                      <button key={opt} onClick={() => setSel((p) => ({ ...p, construction: opt }))} className={chip(sel.construction === opt)}>
                        {t(opt === "Fabric-lined" ? "sustainability.linedLabel" : "sustainability.fullCrochetLabel")}
                      </button>
                    ))}
                  </div>
                  <Desc text={constructionDescs[sel.construction]} id={sel.construction} />
                </div>
              </div>

              {/* Extensions */}
              <div className="grid grid-cols-1 md:grid-cols-[130px_1fr] gap-3 md:gap-5 px-5 py-5">
                <p className="font-body text-xs tracking-[0.22em] uppercase text-gold pt-0.5">
                  {t("sustainability.extensionsLabel")}
                </p>
                <div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(["Sleeves", "Train", "Veil", "Feet"] as const).map((opt) => (
                      <button key={opt} onClick={() => toggleSection(opt)} className={chip(sel.sections.includes(opt))}>
                        {t(`sustainability.${opt === "Sleeves" ? "sleevesLabel" : opt === "Train" ? "trainLabel" : opt === "Veil" ? "veilLabel" : "feetLabel"}`)}
                      </button>
                    ))}
                  </div>
                  <Desc
                    text={sectionDesc(sel.sections)}
                    id={sel.sections.slice().sort().join(",") || "none"}
                  />
                </div>
              </div>

              {/* Material */}
              <div className="grid grid-cols-1 md:grid-cols-[130px_1fr] gap-3 md:gap-5 px-5 py-5">
                <p className="font-body text-xs tracking-[0.22em] uppercase text-gold pt-0.5">
                  {t("sustainability.materialLabel")}
                </p>
                <div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(["Heritage Linen", "Pima Cotton", "Lotus Silk"] as const).map((opt) => (
                      <button key={opt} onClick={() => setSel((p) => ({ ...p, material: opt }))} className={chip(sel.material === opt)}>
                        {t(opt === "Heritage Linen" ? "sustainability.linenLabel" : opt === "Pima Cotton" ? "sustainability.cottonLabel" : "sustainability.silkLabel")}
                      </button>
                    ))}
                  </div>
                  <Desc text={materialDescs[sel.material]} id={sel.material} />
                  <a
                    href="#materials"
                    className="font-body text-[10px] text-gold/50 hover:text-gold transition-colors underline underline-offset-2 block mt-1.5"
                  >
                    {t("sustainability.exploreMaterials")}
                  </a>
                </div>
              </div>

              {/* After the wedding */}
              <div className="grid grid-cols-1 md:grid-cols-[130px_1fr] gap-3 md:gap-5 px-5 py-5">
                <p className="font-body text-xs tracking-[0.22em] uppercase text-gold pt-0.5">
                  {t("sustainability.afterWeddingLabel")}
                </p>
                <div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(["Yours forever", "Just for the day"] as const).map((opt) => (
                      <button key={opt} onClick={() => setSel((p) => ({ ...p, afterWedding: opt }))} className={chip(sel.afterWedding === opt)}>
                        {t(opt === "Yours forever" ? "sustainability.yoursForeverLabel" : "sustainability.justForDayLabel")}
                      </button>
                    ))}
                  </div>
                  <Desc text={afterWeddingDescs[sel.afterWedding]} id={sel.afterWedding} />
                </div>
              </div>

            </div>

            {/* ── Vertical thermometer (desktop) ── */}
            <div className="hidden md:flex flex-col items-center gap-3 pt-3 w-16 shrink-0">
              <p className="font-body text-[8px] tracking-[0.12em] uppercase text-muted-foreground/35 text-center leading-tight">
                {t("sustainability.mostPrecious")}
              </p>

              <div className="relative w-px h-52 bg-border">
                <motion.div
                  className="absolute bottom-0 left-0 right-0 bg-gold"
                  animate={{ height: `${barHeight}%` }}
                  transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                />
                <motion.div
                  className="absolute -left-[3px] w-[7px] h-[7px] rounded-full bg-gold"
                  animate={{ bottom: `calc(${barHeight}% - 3.5px)` }}
                  transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                />
              </div>

              <p className="font-body text-[8px] tracking-[0.12em] uppercase text-muted-foreground/35 text-center leading-tight">
                {t("sustainability.accessible")}
              </p>

              <div className="text-center mt-2">
                {thermoLabel}
              </div>
            </div>

          </div>

          {/* ── Save preferences ── */}
          <div className="mt-5 flex items-center justify-between px-1 min-h-[20px]">
            <AnimatePresence mode="wait">
              {isSaved ? (
                <motion.p
                  key="saved-note"
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="font-body text-[9px] tracking-[0.12em] uppercase text-muted-foreground/35"
                >
                  {t("sustainability.prefAttached")}
                </motion.p>
              ) : savedSel ? (
                <motion.p
                  key="changed-note"
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="font-body text-[9px] tracking-[0.12em] uppercase text-muted-foreground/30"
                >
                  {t("sustainability.prefChanged")}
                </motion.p>
              ) : (
                <motion.span key="empty" />
              )}
            </AnimatePresence>

            <button
              onClick={saveQuotation}
              disabled={isSaved}
              className={`font-body text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 ${
                isSaved
                  ? "text-gold/50 cursor-default"
                  : "text-foreground/40 hover:text-gold cursor-pointer"
              }`}
            >
              {isSaved ? t("sustainability.prefSaved") : savedSel ? t("sustainability.prefUpdate") : t("sustainability.prefSave")}
            </button>
          </div>

          <div className="mt-8 h-px bg-border" />
        </motion.div>

        {/* ══════════════════════════════════════════════════════════════════════
            03 — The Making
        ══════════════════════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-20"
        >
          <div className="flex gap-6 md:gap-10 items-start mb-10">
            {stepNum(t("sustainability.step3Number"))}
            <div className="pt-2 md:pt-4">
              <h3 className="font-display text-2xl md:text-3xl text-foreground mb-4">
                {t("sustainability.step3Title")}
              </h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-md mb-4">
                {t("sustainability.step3Text")}
              </p>
              <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-md">
                {t("sustainability.step3Sub")}
              </p>
            </div>
          </div>

          {/* One-client callout */}
          <motion.div
  initial={{ opacity: 0, x: -20 }}
  whileInView={{ opacity: 1, x: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.9 }}
  className="border-l border-gold/40 pl-8 md:pl-14 py-4"
>
  <p className="font-body text-[9px] tracking-[0.35em] uppercase text-gold mb-6">
    {t("sustainability.step3Commitment")}
  </p>
  <p className="font-display text-2xl md:text-3xl lg:text-4xl text-foreground leading-[1.2] max-w-lg">
    {t("sustainability.step3CommitmentText")}
  </p>
</motion.div>

          <div className="mt-12 h-px bg-border" />
        </motion.div>

        {/* ══════════════════════════════════════════════════════════════════════
            04 — Yours
        ══════════════════════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mb-20"
        >
          <div className="flex gap-6 md:gap-10 items-start">
            {stepNum(t("sustainability.step4Number"))}
            <div className="pt-2 md:pt-4">
              <h3 className="font-display text-2xl md:text-3xl text-foreground mb-4">
                {t("sustainability.step4Title")}
              </h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-md">
                {t("sustainability.step4Text")}
              </p>
            </div>
          </div>
        </motion.div>

        {/* ── Bottom CTA ── */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="pt-16 border-t border-border"
        >
          <p className="font-body text-xs tracking-[0.3em] uppercase text-gold mb-5">
            {t("sustainability.beginCta")}
          </p>
          <h3 className="font-display text-2xl md:text-3xl text-foreground mb-5">
            {t("sustainability.beginSub")}
          </h3>
          <p className="font-body text-sm text-muted-foreground leading-relaxed max-w-xs mb-10">
            {t("sustainability.beginMini")}
          </p>

          {savedSel && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="font-body text-[10px] tracking-[0.12em] uppercase text-gold/50 mb-6"
            >
              {t("sustainability.prefsAttached")}
            </motion.p>
          )}

          <Link
            to="/consultation"
            className="inline-flex items-center justify-center px-10 py-4 bg-gold text-primary-foreground font-body text-xs tracking-[0.2em] uppercase hover:bg-primary-hover transition-colors duration-500"
          >
            {t("sustainability.bookCta")}
          </Link>
        </motion.div>

      </div>
    </section>
  );
};

export default SustainabilitySection;
